<script setup>
import { computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { Spinner } from '@/components/ui/spinner'
import LayerFeaturesListItem from './LayerFeaturesListItem.vue'

const store = useStore()
const selectedRange = computed(() => store.state.layers.selectedRange)
const settings = computed(() => store.getters['user/getUserSettings'])
const map = computed(() => store.getters['geo/getMap'])

const props = defineProps({
  code: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['isLoaded'])

const layer = computed(() => {
  for (const orgId in store.state.layers.orgLayers) {
    const foundLayer = store.state.layers.orgLayers[orgId].find(l => l.uuid === props.code);
    if (foundLayer) {
      return foundLayer;
    }
  }
  return null;
})

const isCollectedDataLayer = computed(() => {
  return layer.value?.properties?.type === 4
})

const isStaticLayer = computed(() => {
  return layer.value?.properties?.type === 5
})

const features = computed(() => {
  if (layer.value?.properties?.features) {
    return layer.value.properties.features;
  }
  return [];
})

const featuresCount = computed(() => {
  return features.value.length;
})

const highlightFeatureOnMap = (feature) => {
  if (!map.value || !feature || !feature.geometry) return;

  let center;
  if (feature.geometry.type === 'Point') {
    center = feature.geometry.coordinates;
  } else if (feature.geometry.type === 'LineString') {
    const coordinates = feature.geometry.coordinates;
    const midIndex = Math.floor(coordinates.length / 2);
    center = coordinates[midIndex];
  } else if (feature.geometry.type === 'Polygon') {
    center = feature.geometry.coordinates[0][0];
  } else {
    return
  }

  map.value.flyTo({
    center: center,
    zoom: 15,
    duration: 800
  });

  store.dispatch('layers/setSelectedFeature', {
    feature: feature,
    coordinates: center,
    isVisible: true
  });
}

watch(
  () => features.value.length,
  (newCount) => {
    if (newCount > 0) {
      emit('isLoaded', true);
    }
  }
)

onMounted(() => {
  if (features.value.length > 0) {
    emit('isLoaded', true);
  }
})
</script>

<template>
  <div>
    <div class="flex items-center justify-between">
      <div>
        <h3 class="font-bold">{{ isStaticLayer ? 'Features' : 'Reports' }} ({{ featuresCount }})</h3>
      </div>
    </div>

    <div v-if="isStaticLayer">
      <div v-if="featuresCount > 0" class="space-y-2.5">
        <LayerFeaturesListItem
          v-for="(feature, index) in features"
          :key="index"
          :feature="feature"
          :settings="settings"
          @highlight="highlightFeatureOnMap"
        />
      </div>
      <div v-else>
        No features found for this static layer.
      </div>
    </div>

    <div v-else-if="isCollectedDataLayer">
      <div v-if="selectedRange && selectedRange.length === 2">
        <div v-if="featuresCount > 0" class="space-y-2.5">
          <LayerFeaturesListItem
            v-for="(feature, index) in features"
            :key="index"
            :feature="feature"
            :settings="settings"
            @highlight="highlightFeatureOnMap"
          />
        </div>
        <div v-else>
          No features found for the selected filters and date range.
        </div>
      </div>
      <div v-else-if="!selectedRange || selectedRange.length !== 2" class="text-muted-foreground text-sm">
        Please select a date range to view features.
      </div>
    </div>

    <div v-else class="text-muted-foreground text-sm">
      This layer type doesn't support feature listing.
    </div>
  </div>
</template>
